import { uniqueId } from 'lodash'
import { ForwardedRef, ReactNode, SyntheticEvent, forwardRef, useContext, useRef, useState } from 'react'
import { Loader } from 'ui'
import { PortalTooltip, useTooltip } from 'ui/src/components/controls/tooltip'
import { ArtboardRef } from '../../artboard-gl/types'
import { UGEContext } from '../context'
import { useCropStyles } from '../hooks/use-crop-styles'
import { UGELayer } from '../types'
import LayerContextMenu from './context-menu'
import { Artboard, Container, LoaderWrapper, PlayPause, Wrapper } from './style'

type Props = {
    children?: ReactNode
    desynchronized?: boolean
    onMouseEnter?: () => void
    onMouseLeave?: () => void
    onPointerDown?: () => void
    onRenderError?: (error: Error) => void
}

const UGEArtboard = (
    {
        children,
        desynchronized,
        onMouseEnter: onMouseEnterProp,
        onMouseLeave: onMouseLeaveProp,
        onPointerDown: onPointerDownProp,
        onRenderError,
    }: Props,
    ref: ForwardedRef<ArtboardRef>
) => {
    const tooltipIdRef = useRef<string>(uniqueId('uge-layer-tooltip-'))
    const { activeEditor, allowedEditors, crop, dimensions, duration, loaded, paused, sourceInfo, setPaused } =
        useContext(UGEContext)
    const [tooltipLayer, setTooltipLayer] = useState<UGELayer | null>(null)
    const [showPlayPause, setShowPlayPause] = useState<boolean>(false)
    const isCropping = activeEditor === 'crop'
    const singleStickerMode = allowedEditors.length === 1 && allowedEditors[0] === 'stickers'
    const [wrapperStyle, artboardStyle] = useCropStyles(isCropping ? undefined : crop)

    const { closeTooltip, listeners, isOpen } = useTooltip({
        color: '#111111',
        direction: 'right',
        id: tooltipIdRef.current,
        triggerType: 'all',
        width: 226,
    })

    function onContextMenu(e: SyntheticEvent<HTMLElement, MouseEvent>, layer: UGELayer) {
        setTooltipLayer(layer)
        return listeners.onContextMenu?.(e)
    }

    function onEllipsisMenu(e: SyntheticEvent<HTMLElement, MouseEvent>, layer: UGELayer) {
        setTooltipLayer(layer)
        return listeners.onClick?.(e)
    }

    function onMouseEnter() {
        setShowPlayPause(true)
        onMouseEnterProp?.()
    }

    function onMouseLeave() {
        setShowPlayPause(false)
        onMouseLeaveProp?.()
    }

    function onPlayPause() {
        setPaused(!paused)
    }

    if (!loaded || !sourceInfo) {
        return (
            <LoaderWrapper style={{ height: dimensions.height, width: dimensions.width }}>
                <Loader size="large" type="blocks" />
            </LoaderWrapper>
        )
    }

    return (
        <Container>
            <Wrapper
                style={{
                    height: dimensions.height,
                    width: dimensions.width,
                    overflow: singleStickerMode ? 'hidden' : undefined,
                    ...wrapperStyle,
                }}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onPointerDownCapture={onPointerDownProp}
            >
                <Artboard
                    desynchronized={desynchronized}
                    fps={sourceInfo.fps}
                    ref={ref}
                    style={artboardStyle}
                    onContextMenu={onContextMenu}
                    onEllipsisMenu={onEllipsisMenu}
                    onRenderError={onRenderError}
                />
                {children}
                {duration > 0 && !isCropping && !singleStickerMode && (
                    <PlayPause
                        hidden={!paused && !showPlayPause}
                        icon={paused ? 'play' : 'pause'}
                        iconScale={1.5}
                        inverted
                        size="small"
                        onClick={onPlayPause}
                    />
                )}
            </Wrapper>
            <PortalTooltip isOpen={isOpen}>
                {tooltipLayer && <LayerContextMenu layer={tooltipLayer} onAfterAction={closeTooltip} />}
            </PortalTooltip>
        </Container>
    )
}

export default forwardRef(UGEArtboard)
